import { lazy } from 'react';

/**
 * lazyWithRetry
 * Enhanced wrapper around React.lazy with automatic retry and stale-chunk recovery.
 *
 * Preempts and resolves the common production pitfall:
 * "Failed to fetch dynamically imported module" (ChunkLoadError after new deployments
 * or momentary network interruptions).
 *
 * Strategy:
 * 1. Attempts dynamic import.
 * 2. On failure, retries once in-memory after a short delay (recovers from network hiccup).
 * 3. If in-memory retry fails, checks sessionStorage to perform a single hard reload
 *    to retrieve the latest manifest / bundle assets.
 * 4. Gracefully unwraps named exports if a module doesn't provide a default export.
 *
 * @param {() => Promise<{ default: React.ComponentType<any> } | any>} componentImport
 * @param {string} [namedExport] - Optional name of the exported component if not default
 * @returns {React.LazyExoticComponent<React.ComponentType<any>>}
 */
export function lazyWithRetry(componentImport, namedExport = null) {
  return lazy(async () => {
    const sessionKey = 'app_last_chunk_reload_timestamp';
    const isBrowser = typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

    const executeImport = async () => {
      const module = await componentImport();
      if (namedExport && module && module[namedExport]) {
        return { default: module[namedExport] };
      }
      if (module && module.default) {
        return module;
      }
      // If module has no default export but has a single export or self
      if (module && typeof module === 'object') {
        const keys = Object.keys(module);
        if (keys.length === 1 && typeof module[keys[0]] === 'function') {
          return { default: module[keys[0]] };
        }
      }
      return module;
    };

    try {
      return await executeImport();
    } catch (primaryError) {
      // Step 1: Retry once after 350ms (handles transient connection drop)
      try {
        await new Promise((resolve) => setTimeout(resolve, 350));
        return await executeImport();
      } catch (retryError) {
        console.warn('[lazyWithRetry] Dynamic import failed on retry:', retryError);

        // Step 2: If running in browser, check if we haven't reloaded recently
        if (isBrowser) {
          const now = Date.now();
          const lastReloadStr = window.sessionStorage.getItem(sessionKey);
          const lastReload = lastReloadStr ? parseInt(lastReloadStr, 10) : 0;

          // Only force reload if at least 15 seconds have passed since last reload
          if (now - lastReload > 15000) {
            window.sessionStorage.setItem(sessionKey, String(now));
            window.location.reload();
            return new Promise(() => {}); // Keep promise pending until reload fires
          }
        }

        // If we already reloaded recently or are in SSR/testing, bubble error to ErrorBoundary
        throw retryError || primaryError;
      }
    }
  });
}

export default lazyWithRetry;
